import React, { useMemo, useState } from "react";
import Heading from "../Common/Heading";
import API, { action } from "../../Api";
import { useLoader } from "../../LoaderContext";

const REPORT_TYPES = [
  { value: "daily_summary", label: "Daily Total & Average" },
  { value: "day_wise", label: "Day-wise Milk Data" },
  { value: "cow_wise_daily", label: "Cow-wise Daily Production" },
  { value: "monthly_summary", label: "Monthly Total & Average" },
  { value: "cow_wise_monthly", label: "Cow-wise Monthly Report" },
  { value: "custom_range_cow_avg", label: "Custom Range Cow-wise Average" },
];

function NewMilkReports() {
  const today = new Date().toISOString().slice(0, 10);
  const thisMonth = new Date().toISOString().slice(0, 7);
  const { setLoading } = useLoader();
  const [reportType, setReportType] = useState("daily_summary");
  const [date, setDate] = useState(today);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [monthValue, setMonthValue] = useState(thisMonth);
  const [cattleId, setCattleId] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState("");

  const columns = useMemo(() => {
    if (!rows.length) return [];
    let keys = Object.keys(rows[0]);
    if (reportType === "cow_wise_daily") {
      keys = keys.filter((key) => key !== "averageMilk");
    }
    return keys;
  }, [rows, reportType]);

  const clearPreviousReport = () => {
    setRows([]);
    setSummary({});
    setError("");
  };

  const getColumnLabel = (key) => {
    const labels = {
      date: "Date",
      totalMilk: "Total Milk",
      morningMilk: "Morning Milk",
      eveningMilk: "Evening Milk",
      averageMilk: "Average Milk",
      totalEntryCount: "Total Entry Count",
      uniqueCattleCount: "Unique Cattle Count",
      uniqueCattleEntryCount: "Unique Cattle Entry Count",
      cattleId: "Cattle ID",
      year: "Year",
      month: "Month",
      fromDate: "From Date",
      toDate: "To Date",
      milkingDays: "Milking Days",
    };
    return labels[key] || key;
  };

  const formatCellValue = (column, value) => {
    if (column === "month" && (reportType === "monthly_summary" || reportType === "cow_wise_monthly")) {
      const monthNumber = Number(value);
      if (Number.isInteger(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
        return new Date(2000, monthNumber - 1, 1).toLocaleString("en-US", { month: "long" });
      }
    }
    return String(value ?? "");
  };

  const escapeCsvCell = (value) => {
    const str = value == null ? "" : String(value);
    const escaped = str.replace(/"/g, '""');
    // Quote if cell contains special CSV characters.
    if (/[",\n\r]/.test(str)) return `"${escaped}"`;
    return escaped;
  };

  const downloadCsv = () => {
    if (!rows.length || !columns.length) return;

    const header = columns.map((col) => escapeCsvCell(getColumnLabel(col))).join(",");
    const csvRows = rows.map((row) =>
      columns.map((col) => escapeCsvCell(formatCellValue(col, row[col]))).join(",")
    );
    const csvContent = [header, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeReportType = reportType.replace(/[^a-z0-9_-]/gi, "_");
    a.href = url;
    a.download = `milk-report-${safeReportType}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const runReport = async () => {
    setLoading(true);
    setError("");
    try {
      let endpoint = null;
      let payload = {};

      if (reportType === "daily_summary") {
        endpoint = API.NEW_DAILY_MILK_SUMMARY;
        payload = { date };
      } else if (reportType === "day_wise") {
        endpoint = API.NEW_DAY_WISE_MILK;
        payload = { fromDate, toDate };
      } else if (reportType === "cow_wise_daily") {
        endpoint = API.NEW_COW_WISE_DAILY_MILK;
        payload = { date };
      } else if (reportType === "monthly_summary") {
        const [selectedYear, selectedMonth] = monthValue.split("-");
        endpoint = API.NEW_MONTHLY_MILK_SUMMARY;
        payload = { year: Number(selectedYear), month: Number(selectedMonth) };
      } else if (reportType === "cow_wise_monthly") {
        const [selectedYear, selectedMonth] = monthValue.split("-");
        endpoint = API.NEW_COW_WISE_MONTHLY_MILK;
        payload = { year: Number(selectedYear), month: Number(selectedMonth) };
      } else if (reportType === "custom_range_cow_avg") {
        endpoint = API.NEW_CUSTOM_RANGE_COW_AVG_MILK;
        payload = {
          fromDate,
          toDate,
          ...(cattleId ? { cattleId } : {}),
        };
      }

      const result = await action(endpoint, payload);
      const apiData = result?.response?.data;

      if (reportType === "monthly_summary" && apiData?.rows) {
        setRows(apiData.rows);
        setSummary(apiData.filter || {});
      } else if (Array.isArray(apiData)) {
        setRows(apiData);
        setSummary({});
      } else {
        setRows(apiData ? [apiData] : []);
        setSummary(apiData || {});
      }
    } catch (e) {
      setRows([]);
      setSummary({});
      setError("Unable to generate report. Please verify filters and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F6FB] py-4 px-2 md:px-4">
      <div className="max-w-6xl mx-auto flex flex-col gap-5 bg-white border border-[#E0E7F3] rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-b border-[#E5ECF7] pb-3">
          <div>
            <Heading title="Milk Reports" />
            <p className="mt-1 text-xs md:text-sm text-gray-500">
              Generate daily, monthly and cow-wise milk summaries with flexible filters.
            </p>
          </div>
          {!!rows.length && (
            <span className="inline-flex items-center rounded-full bg-[#ECF5FF] px-3 py-1 text-xs font-medium text-[#2459A6]">
              {rows.length} record{rows.length > 1 ? "s" : ""} found
            </span>
          )}
        </div>

        <div className="bg-[#F8FAFF] border border-[#DFE8FB] rounded-lg p-3 md:p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#5B77A5]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3B82F6]" />
              <span>Report Filters</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[#41577A]">Report Type</label>
              <select
                className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  clearPreviousReport();
                }}
              >
                {REPORT_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            {(reportType === "daily_summary" || reportType === "cow_wise_daily") && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#41577A]">Date</label>
                <input
                  type="date"
                  className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            )}

            {(reportType === "day_wise" || reportType === "custom_range_cow_avg") && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[#41577A]">From Date</label>
                  <input
                    type="date"
                    className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[#41577A]">To Date</label>
                  <input
                    type="date"
                    className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {(reportType === "monthly_summary" || reportType === "cow_wise_monthly") && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#41577A]">Month</label>
                <input
                  type="month"
                  className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                />
              </div>
            )}

            {reportType === "custom_range_cow_avg" && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#41577A]">Cattle ID (optional)</label>
                <input
                  type="text"
                  className="border border-[#CBD5F1] rounded-md px-2.5 py-2 text-sm bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  value={cattleId}
                  onChange={(e) => setCattleId(e.target.value)}
                  placeholder="Enter cattle tag / ID"
                />
              </div>
            )}

            <div className="flex items-end">
              <button
                type="button"
                className="w-full md:w-auto inline-flex justify-center items-center gap-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white px-4 py-2.5 text-sm font-medium rounded-md shadow-sm transition-colors"
                onClick={runReport}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {!!error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs md:text-sm rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {!!Object.keys(summary || {}).length && (
          <div className="space-y-2">
            <div className="text-xs font-semibold tracking-wide uppercase text-[#5B77A5]">
              Active Filters
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(summary).map(([key, value]) => (
                <div
                  key={key}
                  className="bg-[#EEF5FF] border border-[#D3E3FB] rounded-lg px-3 py-2.5"
                >
                  <div className="text-[10px] font-semibold text-[#4A6A92] uppercase tracking-wide">
                    {key}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-[#1F3D63]">
                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {rows.length === 0 && !error && (
          <div className="text-xs md:text-sm text-gray-500 border border-dashed border-[#CBD5F1] rounded-lg px-4 py-6 text-center bg-[#F9FBFF]">
            Select a report type, configure filters above, then click{" "}
            <span className="font-semibold text-gray-700">Generate Report</span> to view data.
          </div>
        )}

        {!!rows.length && (
          <div className="border border-[#E1E7F5] rounded-lg bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E1E7F5]">
              <div className="text-xs font-semibold tracking-wide uppercase text-[#5B77A5]">
                Report Table
              </div>
              <button
                type="button"
                onClick={downloadCsv}
                className="inline-flex justify-center items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-3 py-2 text-xs font-medium rounded-md shadow-sm"
              >
                Download CSV
              </button>
            </div>
            <div className="max-h-[70vh] overflow-auto">
              <table className="min-w-full text-xs md:text-sm">
                <thead className="sticky top-0 z-[1] bg-[#E7F0FF]">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="text-left px-3 py-2 border-b border-[#D4E0F7] text-[11px] md:text-xs font-semibold tracking-wide text-[#325B97] uppercase"
                      >
                        {getColumnLabel(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`
                        border-b border-[#ECF0FA]
                        ${idx % 2 === 0 ? "bg-white" : "bg-[#F9FBFF]"}
                        hover:bg-[#EEF4FF]
                      `}
                    >
                      {columns.map((col) => (
                        <td
                          key={`${idx}-${col}`}
                          className="px-3 py-2 align-middle text-[11px] md:text-sm text-gray-700 whitespace-nowrap"
                        >
                          {formatCellValue(col, row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NewMilkReports;

